'use client';

// <RelativeTime> — a self-updating "2 hours ago" / "in 5 minutes" label
// (BL-596). Wraps @auctogon/time's `formatRelative` and re-renders on an
// interval so the label stays fresh without the caller wiring a timer.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { formatRelative, type FormatRelativeOptions, type TimeInput } from '@auctogon/time';
import { toIso } from './time-utils';

export interface RelativeTimeProps {
  /** The instant to describe relative to now. */
  instant: TimeInput;
  /** Refresh cadence in ms. Default 60s — relative labels rarely need finer. */
  intervalMs?: number;
  options?: FormatRelativeOptions;
  className?: string;
  /**
   * Hover text — the absolute instant, formatted by the caller in the zone its
   * surface leads with (ADR 024 — Time Model). This component stays zone-free.
   */
  title?: string;
  /** Automation handle for a surface whose stamp is what a check targets. */
  'data-testid'?: string;
}

/** Render a live-updating relative-time label inside a semantic `<time>`. */
export function RelativeTime({
  instant,
  intervalMs = 60_000,
  options,
  className,
  title,
  'data-testid': testId,
}: RelativeTimeProps): ReactElement {
  // Keep the latest `options` in a ref so an inline object literal from the
  // caller doesn't reset the interval each render, while still being read fresh
  // on every tick (mirrors LiveCountdown's `nowMs` handling).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [label, setLabel] = useState(() =>
    formatRelative(instant, Date.now(), optionsRef.current),
  );

  useEffect(() => {
    const tick = () => setLabel(formatRelative(instant, Date.now(), optionsRef.current));
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [instant, intervalMs]);

  const iso = toIso(instant);
  return iso ? (
    <time dateTime={iso} className={className} title={title} data-testid={testId}>
      {label}
    </time>
  ) : (
    <span className={className} title={title} data-testid={testId}>
      {label}
    </span>
  );
}
